import { useState } from "react"
import { Button } from "@chakra-ui/react"
import { FaDownload } from "react-icons/fa6"
import { nowDateTime } from "@hydrologiq/common/helpers/date"
import { useRestApi } from "@hydrologiq/shared/providers/helpers/useRestApi"
import { OrderFilters, OrderSort } from "../providers/hooks/useOrders"

interface OrdersExportProps {
  filters: OrderFilters
  sort: OrderSort | null
  disabled?: boolean
  variant?: "hydrologiq_primary" | "hydrologiq_secondary" | "hydrologiq_icon"
  size?: "sm" | "md" | "lg"
}

function OrdersExport({
  filters,
  sort,
  disabled = false,
  variant = "hydrologiq_secondary",
  size = "sm",
}: OrdersExportProps) {
  const [isExporting, setIsExporting] = useState(false)
  const restApi = useRestApi()

  const generateFilename = () => {
    const now = nowDateTime()
    const timestamp = now.format("YYYY-MM-DD_HH-mm-ss")
    return `logiq_orders_export_${timestamp}.csv`
  }

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)

    try {
      // Build parameters for the REST endpoint
      const params: Record<string, string | boolean> = {
        download: true,
      }

      // Add filter parameters (only include non-empty values)
      if (filters.status) params.status = filters.status
      if (filters.customer) params.customer = filters.customer
      if (filters.site) params.site = filters.site
      // if (filters.trailer) params.trailer = filters.trailer
      if (filters.name) params.name = filters.name
      if (filters.latest_event_status) params.latest_event_status = filters.latest_event_status

      // Add sort parameter
      if (sort?.field && sort?.direction) {
        params.sort = `${sort.field}:${sort.direction}`
      }

      // Add filename parameter
      params.filename = generateFilename()

      // Use the REST API provider's download method
      await restApi.download("/orders/export", { params })
    } catch (error) {
      console.error("Export failed:", error)
      // TODO: Show error toast/notification
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant={variant as any}
      size={size}
      onClick={handleExport}
      disabled={disabled || isExporting}
      loading={isExporting}
    >
      <FaDownload />
      {isExporting ? "Exporting..." : "Export CSV"}
    </Button>
  )
}

export default OrdersExport
